/**
 * SaveManager v2. Persiste nível/XP, essências, upgrades do altar, SETTINGS
 * (idioma + áudio) e PROGRESSO (elites, gate, bosses, Alpha Soul).
 * Migração tolerante: saves v1 (só idioma no topo) carregam sem quebrar; campos
 * ausentes viram default. Salva em eventos-chave + autosave.
 */
import { GameConfig } from "../config/GameConfig";
import { events } from "../core/Events";
import { i18n } from "../i18n/i18n";
import { type EssenceType, ESSENCE_TYPES } from "../essence/EssenceTypes";
import type { EssenceInventory } from "../essence/EssenceInventory";
import type { VoidAltar } from "../world/VoidAltar";
import type { XPSystem } from "../progression/XPSystem";
import type { GameProgress, ProgressSnapshot } from "../progression/GameProgress";
import type { AudioManager, AudioSettings } from "../audio/AudioManager";
import type { Inventory, InventorySnapshot } from "../items/Inventory";
import { clampCamZoom, CAM_ZOOM_DEFAULT, type Accessibility, type AccessibilitySettings } from "../core/Accessibility";
import { quality, type QualityPreset } from "../core/Quality";
import type { VoidTreeState } from "../progression/VoidTree";
import { FogOfWar } from "../world/FogOfWar";
import type { RegionManager } from "../world/RegionManager";
import type { RegionMastery, RegionMasterySnapshot } from "../progression/RegionMastery";
import type { SaveStorage } from "./Storage";

export interface SaveData {
  version: number;
  /** 10.9V — token de RESET GLOBAL: se != GameConfig.save.wipeToken, o save começa ZERADO
   * (mantém só as configurações). Bumpar o token no config zera TODOS os jogadores 1x. */
  wipeToken: number;
  level: number;
  xp: number;
  essence: Record<EssenceType, number>;
  altarLevels: Record<EssenceType, number>;
  settings: { language: string; audio: AudioSettings; access: AccessibilitySettings; graphics: QualityPreset; graphicsAuto: boolean };
  progress: ProgressSnapshot;
  inventory: InventorySnapshot | undefined; // v3 (Inventory.load sanitiza)
  fog: number[]; // v4: legado (fog da Região 1)
  voidTree: Record<string, number>; // v5: ranks (pontos derivados de level+boss)
  // v6: multi-região
  unlockedRegions: string[];
  currentRegion: string;
  fogByRegion: Record<string, number[]>;
  /** 10.5M — assinatura do grid do fog: se mudou, o fog salvo é descartado (não corrompe). */
  fogSig?: string;
  /** 10.9HQ — extensão do grid de origem quando o save é do grid antigo (migração). */
  fogFromExtent?: number;
  discoveriesByRegion: Record<string, Record<string, boolean>>;
  // v8: Regional Mastery (xp/nível por região)
  regionMastery: RegionMasterySnapshot;
  // 10.6R: correio (tipagem relaxada p/ compat com saves antigos)
  mail?: { messages: Record<string, unknown>[]; lastDailyDay: string; sentKeys?: string[] };
}

interface SaveDeps {
  xp: XPSystem;
  inventory: EssenceInventory;
  altar: VoidAltar;
  audio: AudioManager;
  progress: GameProgress;
  equipment: Inventory;
  region: RegionManager;
  access: Accessibility;
  tree: VoidTreeState;
  regionMastery: RegionMastery;
  mail?: { snapshot(): { messages: Record<string, unknown>[]; lastDailyDay: string; sentKeys?: string[] } }; // 10.6R
}

function intOr(v: unknown, fallback: number): number {
  return typeof v === "number" && Number.isFinite(v) ? Math.floor(v) : fallback;
}
function clampInt(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
function numOr(v: unknown, fallback: number): number {
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}
function fillEssence(data: unknown): Record<EssenceType, number> {
  const src = (data ?? {}) as Partial<Record<EssenceType, number>>;
  const out = {} as Record<EssenceType, number>;
  for (const t of ESSENCE_TYPES) out[t] = Math.max(0, intOr(src[t], 0));
  return out;
}

export class SaveManager {
  private readonly cfg = GameConfig.save;
  private autosaveTimer = 0;
  private restoring = false;

  restore(apply: () => void): void {
    this.restoring = true;
    try { apply(); } finally { this.restoring = false; }
  }

  constructor(
    private readonly storage: SaveStorage,
    private readonly deps: SaveDeps,
  ) {
    events.on("level:up", () => this.save());
    events.on("altar:upgraded", () => this.save());
    events.on("settings:changed", () => this.save());
    i18n.onChange(() => this.save());
  }

  buildData(): SaveData {
    const state = this.deps.xp.getState();
    const world = this.deps.region.snapshot();
    return {
      version: this.cfg.version,
      wipeToken: GameConfig.save.wipeToken, // 10.9V
      level: state.level,
      xp: state.xp,
      essence: this.deps.inventory.snapshot(),
      altarLevels: this.deps.altar.snapshot(),
      settings: { language: i18n.locale, audio: this.deps.audio.snapshot(), access: this.deps.access.snapshot(), graphics: quality.name, graphicsAuto: quality.auto },
      progress: this.deps.progress.snapshot(),
      inventory: this.deps.equipment.snapshot(),
      fog: world.fogByRegion["forgottenForest"] ?? [], // legado (compat)
      voidTree: this.deps.tree.snapshot(),
      unlockedRegions: world.unlockedRegions,
      currentRegion: world.currentRegion,
      fogByRegion: world.fogByRegion,
      fogSig: FogOfWar.signature(), // 10.5M
      discoveriesByRegion: world.discoveriesByRegion,
      regionMastery: this.deps.regionMastery.snapshot(),
      mail: this.deps.mail?.snapshot(),
    };
  }

  save(): void {
    if (this.restoring) return;
    this.storage.save(this.cfg.key, JSON.stringify(this.buildData()));
  }

  /**
   * 10.5W — BACKUP do progresso em CÓDIGO (o save é local: trocar de celular perdia tudo).
   * Exporta o save inteiro em base64 para o jogador guardar/colar em outro aparelho.
   */
  exportCode(): string {
    const json = JSON.stringify(this.buildData());
    return btoa(unescape(encodeURIComponent(json)));
  }

  /** Restaura de um código. Valida o JSON antes de gravar; false se o código for inválido. */
  importCode(code: string): boolean {
    try {
      const json = decodeURIComponent(escape(atob(code.trim())));
      const data = JSON.parse(json) as Record<string, unknown>;
      if (typeof data !== "object" || data === null || typeof data.level !== "number") return false;
      this.storage.save(this.cfg.key, JSON.stringify(this.migrate(data)));
      return true;
    } catch {
      return false;
    }
  }

  /** Lê e migra; null se não houver save (ou ilegível). */
  load(): SaveData | null {
    const raw = this.storage.load(this.cfg.key);
    if (!raw) return null;
    try {
      const data = JSON.parse(raw) as Record<string, unknown>;
      // 10.9V — RESET GLOBAL controlado: token diferente = save começa ZERADO (mantém só as
      // configurações). Bumpar GameConfig.save.wipeToken zera todos os testadores 1x na próxima abertura.
      const savedToken = typeof data.wipeToken === "number" ? data.wipeToken : 0;
      if (savedToken !== GameConfig.save.wipeToken) {
        const fresh = this.migrate({ settings: data.settings }); // preserva idioma/áudio/gráficos
        this.storage.save(this.cfg.key, JSON.stringify(fresh)); // grava o zerado (com o token novo)
        return fresh;
      }
      return this.migrate(data);
    } catch {
      return null;
    }
  }

  /** 10.9X — SaveData PADRÃO (personagem novo), preservando as CONFIGURAÇÕES atuais (idioma/áudio/
   * gráficos são globais, não do personagem). Usado ao criar um personagem por classe. */
  freshData(): SaveData {
    const settings = { language: i18n.locale, audio: this.deps.audio.snapshot(), access: this.deps.access.snapshot(), graphics: quality.name, graphicsAuto: quality.auto };
    return this.migrate({ settings });
  }

  private migrate(data: unknown): SaveData {
    const d = (data ?? {}) as Record<string, unknown>;
    const dsettings = (d.settings ?? {}) as {
      language?: unknown;
      audio?: Partial<AudioSettings>;
      access?: Partial<AccessibilitySettings>;
      graphics?: unknown;
      graphicsAuto?: unknown; // 10.9HY
    };
    const dprogress = (d.progress ?? {}) as Partial<ProgressSnapshot>;
    const legacyLang = typeof d.language === "string" ? d.language : undefined; // save v1
    const ad = GameConfig.audio.defaults;
    const legacyFog = Array.isArray(d.fog) ? (d.fog as number[]).filter((n) => typeof n === "number" && Number.isFinite(n)) : [];

    // teto sanitário: um save adulterado com level absurdo NÃO pode travar o replay de bônus
    const MAX_LEVEL = 999;
    return {
      version: this.cfg.version,
      wipeToken: GameConfig.save.wipeToken, // 10.9V
      level: clampInt(intOr(d.level, GameConfig.progression.startLevel), GameConfig.progression.startLevel, MAX_LEVEL),
      xp: Math.max(0, intOr(d.xp, 0)),
      essence: fillEssence(d.essence),
      altarLevels: fillEssence(d.altarLevels),
      settings: {
        language: typeof dsettings.language === "string" ? dsettings.language : (legacyLang ?? i18n.locale),
        audio: {
          master: numOr(dsettings.audio?.master, ad.master),
          music: numOr(dsettings.audio?.music, ad.music),
          sfx: numOr(dsettings.audio?.sfx, ad.sfx),
        },
        access: {
          uiScale: (["small", "default", "large"].includes(String(dsettings.access?.uiScale)) ? dsettings.access!.uiScale : "default") as AccessibilitySettings["uiScale"],
          reducedMotion: dsettings.access?.reducedMotion === true,
          stickSide: dsettings.access?.stickSide === "left" ? "left" : "right", // 10.5Y
          camZoom: clampCamZoom(numOr(dsettings.access?.camZoom, CAM_ZOOM_DEFAULT)), // 10.6C
        },
        graphics: (["low", "medium", "high"].includes(String(dsettings.graphics)) ? dsettings.graphics : GameConfig.quality.default) as QualityPreset,
        // 10.9HY — true = nunca escolheu à mão, então o preset vem da detecção do aparelho.
        // Save antigo (sem o campo) conta como AUTO: o aparelho passa a mandar, que é o objetivo.
        graphicsAuto: dsettings.graphicsAuto !== false,
      },
      progress: {
        // 10.7K — BUG FIX: o migrate reconstruía campo-a-campo e ESQUECIA diamonds, vipTier,
        // vipAreasUnlocked, absorbRegen, passExpiresAt, depth, areaGoals, territories, daily,
        // areaBossKills, bossDay, bossKillsToday → resetavam a cada launch (diamantes sumiam!).
        // O spread preserva TODOS os campos do save; GameProgress.load() sanitiza tudo depois.
        // Também RECUPERA os diamantes/VIP dos saves já existentes.
        ...dprogress,
        autoSellRarity: typeof dprogress.autoSellRarity === "string" ? dprogress.autoSellRarity : "off", // 10.9M
        elitesDefeated: Math.max(0, intOr(dprogress.elitesDefeated, 0)),
        alphaGateOpen: dprogress.alphaGateOpen === true,
        bossesDefeated: {
          voidAlpha: dprogress.bossesDefeated?.voidAlpha === true,
          aetherColossus: dprogress.bossesDefeated?.aetherColossus === true, // v7
        },
        alphaSoul: Math.max(0, intOr(dprogress.alphaSoul, 0)),
        discoveries: {
          sanctuary: dprogress.discoveries?.sanctuary === true,
          altar: dprogress.discoveries?.altar === true,
          alphaDen: dprogress.discoveries?.alphaDen === true,
        },
        wardensDefeated: Math.max(0, intOr(dprogress.wardensDefeated, 0)), // v7
        aetherCore: Math.max(0, intOr(dprogress.aetherCore, 0)), // v7
        absorbHp: Math.max(0, intOr(dprogress.absorbHp, 0)), // 10.4O
        absorbPower: Math.max(0, intOr(dprogress.absorbPower, 0)), // 10.4O
        coins: Math.max(0, intOr(dprogress.coins, 0)), // 10.4Y
        shopBought: (dprogress.shopBought && typeof dprogress.shopBought === "object" ? dprogress.shopBought : {}) as Record<string, number>, // 10.4Y
        outfit: typeof dprogress.outfit === "string" ? dprogress.outfit : "void", // 10.4Z
        outfitsOwned: Array.isArray(dprogress.outfitsOwned) ? (dprogress.outfitsOwned as string[]).filter((s) => typeof s === "string") : ["void"], // 10.4Z
        petOwned: dprogress.petOwned === true, // 10.5C
        voidPointsBought: Math.max(0, intOr(dprogress.voidPointsBought, 0)), // 10.5L (default 0 em saves antigos)
        missions: dprogress.missions && typeof dprogress.missions === "object" ? dprogress.missions : undefined, // 10.5O
        tips: Array.isArray(dprogress.tips) ? (dprogress.tips as string[]).filter((t) => typeof t === "string") : [], // 10.5U
      },
      // v3: inventário (undefined em saves v2 -> Inventory.load aplica defaults)
      inventory: (d.inventory as InventorySnapshot | undefined) ?? undefined,
      // v4: fog of war (ausente em saves antigos -> vazio)
      fog: legacyFog,
      // v5: void tree ranks (VoidTreeState.load valida/clampa)
      voidTree: d.voidTree && typeof d.voidTree === "object" ? (d.voidTree as Record<string, number>) : {},
      // v6: multi-região (semeia a Forgotten Forest a partir do save legado)
      unlockedRegions: this.migrateUnlocked(d, dprogress),
      currentRegion: typeof d.currentRegion === "string" ? (d.currentRegion as string) : "forgottenForest",
      fogSig: typeof d.fogSig === "string" ? (d.fogSig as string) : undefined,
      // 10.9HQ — o grid antigo (fixo, 10x112) é MIGRADO pro grid por região: o explorado não se perde
      fogFromExtent: d.fogSig === FogOfWar.signature() ? undefined : GameConfig.fog.worldExtent,
      fogByRegion:
        d.fogByRegion && typeof d.fogByRegion === "object" && (d.fogSig === FogOfWar.signature() || d.fogSig === FogOfWar.LEGACY_SIGNATURE)
          ? (d.fogByRegion as Record<string, number[]>)
          : { forgottenForest: legacyFog },
      discoveriesByRegion:
        d.discoveriesByRegion && typeof d.discoveriesByRegion === "object"
          ? (d.discoveriesByRegion as Record<string, Record<string, boolean>>)
          : {
              forgottenForest: {
                sanctuary: dprogress.discoveries?.sanctuary === true,
                altar: dprogress.discoveries?.altar === true,
                alphaDen: dprogress.discoveries?.alphaDen === true,
              },
            },
      // v8: Regional Mastery (RegionMastery.load valida/clampa)
      regionMastery: d.regionMastery && typeof d.regionMastery === "object" ? (d.regionMastery as RegionMasterySnapshot) : {},
      // 10.8I — BUG FIX: o migrate DROPAVA o `mail` → todo load o correio voltava vazio e o
      // sendWelcome/checkDailyLogin re-davam a recompensa ("recebo recompensa toda vez que entro").
      // Preserva o correio salvo (Mail.load valida/sanitiza cada mensagem).
      mail: d.mail && typeof d.mail === "object" ? (d.mail as SaveData["mail"]) : undefined,
    };
  }

  /** Regiões desbloqueadas: armazenadas + derivadas dos bosses (aplica SEMPRE os
   * desbloqueios por boss por cima do que está salvo — inclui a migração v7→v8 que
   * concede Tempest Reach a quem já derrotou o Aether Colossus, §65). */
  private migrateUnlocked(d: Record<string, unknown>, dprogress: Partial<ProgressSnapshot>): string[] {
    const set = new Set<string>(["forgottenForest"]);
    if (Array.isArray(d.unlockedRegions)) {
      for (const x of d.unlockedRegions as unknown[]) if (typeof x === "string") set.add(x);
    }
    // desbloqueios derivados do progresso (garantem consistência entre versões)
    if (dprogress.bossesDefeated?.voidAlpha === true) set.add("shatteredWilds");
    if (dprogress.bossesDefeated?.aetherColossus === true) set.add("tempestReach");
    return [...set];
  }

  reset(): void {
    this.storage.remove(this.cfg.key);
    this.autosaveTimer = 0;
  }

  updateAutosave(dt: number): void {
    this.autosaveTimer += dt;
    if (this.autosaveTimer >= this.cfg.autosaveInterval) {
      this.autosaveTimer = 0;
      this.save();
    }
  }
}
